import { z } from 'zod';

import type { TFunction } from 'i18next';

import {
  customFieldValuesSchema,
  refineClientOrGuest,
  refineRequiredCustomFields,
  requiredSelect,
} from './common';

export type BookingGroupClassKind = 'single' | 'recurring';

const isPositiveNumber = (value: string) => Number(value) > 0;

// No duration: a new class lasts as long as its service, and only the edit forms override it.
// No start either: a new class picks one of the trainer's free slots, the edit forms a bare minute.
const groupClassBaseFields = (t: TFunction) => ({
  staff_id: requiredSelect(t),
  capacity: z
    .string()
    .min(1, { message: t('validation_required') })
    .refine(isPositiveNumber, { message: t('validation_too_small') }),
  online_bookable: z.boolean(),
  note: z.string(),
});

const recurrenceFields = (t: TFunction) => ({
  weekdays: z.array(z.number()),
  week_frequency: z
    .string()
    .min(1, { message: t('validation_required') })
    .refine(isPositiveNumber, { message: t('validation_too_small') }),
  effective_from: z.string().min(1, { message: t('validation_required') }),
  effective_until: z.string(),
});

const isValidityWindowOrdered = (values: {
  effective_from: string;
  effective_until: string;
}) =>
  !values.effective_until || values.effective_until >= values.effective_from;

export const createBookingGroupClassSchema = (
  t: TFunction,
  kind: BookingGroupClassKind = 'single',
) =>
  z
    .object({
      ...groupClassBaseFields(t),
      ...recurrenceFields(t),
      service_id: requiredSelect(t),
      starts_at: z.string().min(1, { message: t('validation_required') }),
      date: z.string(),
      week_frequency: z.string(),
      effective_from: z.string(),
    })
    .superRefine((values, ctx) => {
      if (kind === 'single') {
        if (!values.date) {
          ctx.addIssue({
            code: 'custom',
            path: ['date'],
            message: t('validation_required'),
          });
        }
        return;
      }

      if (values.weekdays.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['weekdays'],
          message: t('validation_required'),
        });
      }
      if (!isPositiveNumber(values.week_frequency)) {
        ctx.addIssue({
          code: 'custom',
          path: ['week_frequency'],
          message: t('validation_too_small'),
        });
      }
      if (!values.effective_from) {
        ctx.addIssue({
          code: 'custom',
          path: ['effective_from'],
          message: t('validation_required'),
        });
      }
      if (!isValidityWindowOrdered(values)) {
        ctx.addIssue({
          code: 'custom',
          path: ['effective_until'],
          message: t('validation_invalid_date'),
        });
      }
    });

export const createBookingGroupSessionSchema = (t: TFunction) =>
  z.object({
    ...groupClassBaseFields(t),
    start_minute: requiredSelect(t),
    duration_minutes: requiredSelect(t),
    location_id: requiredSelect(t),
    date: z.string().min(1, { message: t('validation_required') }),
  });

export const createBookingGroupParticipantSchema = (
  t: TFunction,
  requiredCustomFieldIds: number[] = [],
) =>
  z
    .object({
      client_id: z.string(),
      guest_info: z.string(),
      coverage: z.string(),
      note: z.string(),
      staff_comment: z.string(),
      custom_field_values: customFieldValuesSchema,
    })
    .superRefine((values, ctx) => {
      refineClientOrGuest(t)(values, ctx);
      refineRequiredCustomFields(t, requiredCustomFieldIds)(values, ctx);
    });

export type BookingGroupClassFormValues = z.infer<
  ReturnType<typeof createBookingGroupClassSchema>
>;

export type BookingGroupSessionFormValues = z.infer<
  ReturnType<typeof createBookingGroupSessionSchema>
>;

export type BookingGroupParticipantFormValues = z.infer<
  ReturnType<typeof createBookingGroupParticipantSchema>
>;

export const createBookingGroupScheduleCapacityFormSchema = (t: TFunction) =>
  z.object({ capacity: groupClassBaseFields(t).capacity });

export type BookingGroupScheduleCapacityFormValues = z.infer<
  ReturnType<typeof createBookingGroupScheduleCapacityFormSchema>
>;
